<template>
  <div>
    <tabs-container v-model="groupTabsProxy" v-if="group_tabs.length > 0" />
    <div
      class="overflow-x-auto"
      v-for="quality_template_group in quality_template?.quality_template_groups"
      v-show="quality_template_group.quality_group == current_group"
      :key="quality_template_group.id"
    >
      <template v-if="general_values">
        <general-group-grid
          v-model="general_values"
          :quality_template="quality_template"
          :quality_group="quality_template_group.quality_group"
        />
      </template>
    </div>
  </div>
</template>

<script>
import GeneralGroupGrid from "@/components/views/layout/quality_control/GeneralGroupGrid.vue";
import QualityGeneralMixin from "@/components/views/layout/quality_control/mixins/quality_general.js";
import QualityAttributeMixin from "@/components/views/mixins/models/quality_attribute.js";

export default {
  components: {
    GeneralGroupGrid,
  },
  mixins: [QualityGeneralMixin, QualityAttributeMixin],
  props: {
    quality_template: { required: true },
    modelValue: { required: true },
    default_quality_generals: { required: false, type: Array },
  },
  data() {
    return {
      general_values: null,
      group_tabs: [],
    };
  },
  computed: {
    all_attributes: function () {
      if (this.quality_template)
        return this.quality_groups
          .map((group) => group.quality_attributes)
          .flat();
      return [];
    },
    parsed_general_values: function () {
      return this.parseGeneralValues();
    },
    current_group: function () {
      const current_tab = this.group_tabs.find((opt) => opt.current);
      return this.quality_groups.find((group) => group?.id == current_tab?.id);
    },
    quality_groups: function () {
      let quality_groups = [];
      if (
        this.quality_template &&
        this.quality_template.quality_template_groups
      )
        quality_groups = this.quality_template.quality_template_groups.map(
          (tp) => tp.quality_group
        );
      return quality_groups;
    },
    groupTabsProxy: {
      // se hizo una modificación en TabContainer, que vuelve el modelo a objeto, esto lo resuelve temporalmente
      get() {
        // Si ya es un array, lo retorna. Si no, lo convierte usando Object.values.
        return Array.isArray(this.group_tabs)
          ? this.group_tabs
          : Object.values(this.group_tabs);
      },
      set(newValue) {
        // Aseguramos que siempre se guarde como array
        this.group_tabs = Array.isArray(newValue)
          ? newValue
          : Object.values(newValue);
      }
    },
  },
  created() {
    this.fillTabs();
    this.fillGeneralValues();
  },
  methods: {
    parseGeneralValues: function () {
      if (!this.quality_template) return [];
      let quality_generals = [];
      const group_ids = Object.keys(this.general_values);
      for (let group_index = 0; group_index < group_ids.length; group_index++) {
        const group_id = group_ids[group_index];
        const group = this.quality_groups.find(
          (group) => group?.id == group_id
        );
        const attributes = group.quality_attributes;
        for (
          let attribute_index = 0;
          attribute_index < attributes.length;
          attribute_index++
        ) {
          const attribute = attributes[attribute_index];
          let value = this.getGeneralValue(group, attribute);
          let value_float;
          let value_bool;
          if (value && this.qualityAttributeHasFloatValue(attribute))
            value_float = Number(value) ?? 0;
          if (this.qualityAttributeIsBoolean(attribute))
            value_bool = Boolean(value);
          let percent = Number(
            this.getPercentageGeneralValue(group, attribute)
          );
          percent = percent ? percent / 100 : null;
          let def = this.default_quality_generals?.find(
            (general) => general.quality_attribute_id == attribute.id
          );
          let quality_general = {
            id: def?.id ?? null,
            quality_attribute_id: attribute.id,
            value: value,
            value_float: value_float ?? null,
            value_bool: value_bool ?? null,
            percent_total_by_group: percent ?? null,
            _destroy: null,
          };
          quality_general._destroy =
            Boolean(def) && this.destroyableValue(quality_general, attribute);
          quality_generals.push(quality_general);
        }
      }
      const filtered_quality_generals = quality_generals.filter(
        (quality_general) =>
          Boolean(quality_general.id) || !this.destroyableValue(quality_general)
      );
      return filtered_quality_generals;
    },
    /**
     * identifica si el valor debe guardarse en la bd o no
     **/
    destroyableValue: function (quality_general, quality_attribute = null) {
      if (!quality_attribute)
        quality_attribute = this.all_attributes.find(
          (attribute) => attribute.id == quality_general.quality_attribute_id
        );
      const value = this.computedDetailValue(
        quality_general,
        quality_attribute
      );
      return !value;
    },
    /**
     * el valor del quality_sampling_detail que se usara para las operaciones
     * depende de su crop_attribute_type_id
     **/
    computedDetailValue: function (quality_general, quality_attribute = null) {
      if (!quality_attribute)
        quality_attribute = this.all_attributes.find(
          (attribute) => attribute.id == quality_general.quality_attribute_id
        );
      if (this.qualityAttributeIsBoolean(quality_attribute)) {
        return quality_general.value_bool;
      } else if (this.qualityAttributeIsContinous(quality_attribute)) {
        return quality_general.value_float;
      } else {
        return quality_general.value;
      }
    },
    fillTabs: function () {
      this.group_tabs = [];
      for (let i = 0; i < this.quality_groups.length; i++) {
        const group = this.quality_groups[i];
        this.group_tabs.push({
          id: group.id,
          name: group.name,
          current: i === 0,
        });
      }
    },
    fillGeneralValues: async function () {
      const quality_groups = this.quality_groups ?? [];
      if (quality_groups.length == 0) return;

      let general_values = {};
      for (let g = 0; g < quality_groups.length; g++) {
        let group = quality_groups[g];
        general_values[group.id] = {};
        let attributes = group.quality_attributes;
        for (let a = 0; a < attributes.length; a++) {
          const attribute = attributes[a];
          general_values[group.id][attribute.id] = null;
        }
      }

      this.general_values = general_values;
      if (this.default_quality_generals) this.setDefaultGeneralValues();
    },
    setDefaultGeneralValues: function () {
      for (let group_i = 0; group_i < this.quality_groups.length; group_i++) {
        const group = this.quality_groups[group_i];
        for (
          let attr_i = 0;
          attr_i < group.quality_attributes.length;
          attr_i++
        ) {
          const attr = group.quality_attributes[attr_i];
          const value = this.getDefaultGeneralValue(attr?.id);
          if (value) this.general_values[group.id][attr.id] = value;
        }
      }
    },
    getDefaultGeneralValue: function (attr_id) {
      let quality_general = this.default_quality_generals?.find(
        (s) => s.quality_attribute_id == attr_id
      );
      if (!quality_general) return;

      return this.computedDetailValue(quality_general);
    },
  },
  watch: {
    general_values: {
      handler: function () {
        let quality_general = this.parseGeneralValues();
        this.$emit("update:modelValue", quality_general);
      },
      deep: true,
    },
  },
};
</script>
